// ============================================================
// exposureBuilder - the exposure picker's codes, as pure functions
// ============================================================
// What a preset pick and a custom build write, given the shared "who
// reads it / against what / how many hits" fields. AddExposureModal and
// its parts render; this decides the `Ex:` code.

import {
  AXES,
  buildExposureCode,
  presetCode,
  parseExposure,
  type Axis,
  type ExposureScope,
  type ExposureMode,
} from '../engine/exposures';

/** The fields every pick shares. */
export interface SharedExposureFields {
  scope: ExposureScope;
  /** '' = any attack. */
  mode: '' | ExposureMode;
  /** Hits it survives; 0 = unlimited. */
  uses: number;
}

/** A code narrowed to one kind of attack (v8.368.7); unchanged for any attack. */
function withMode(code: string, mode: '' | ExposureMode): string {
  if (!mode) return code;
  const ex = parseExposure(code);
  return ex ? buildExposureCode({ ...ex, mode }) : code;
}

/** A preset's code for `rounds` (0 = until removed), under the shared fields. */
export function presetExposureCode(id: string, rounds: number, shared: SharedExposureFields): string {
  const { scope, mode, uses } = shared;
  return withMode(presetCode(id, { rounds, scope, ...(uses ? { uses } : {}) }), mode);
}

/** The custom builder as typed. */
export interface CustomExposure {
  axis: Axis;
  value: number;
  expr: string;
  condCode: string;
  loc: string;
  /** An aura (v8.368.8): read within this many metres; 0 = worn, not radiated. */
  radius: number;
  cap: number;
  rounds: number;
}

type AxisValue = number | true | string;

/** What each kind of axis carries: a flag nothing, the rest their own field. */
const AXIS_VALUE: Record<string, (c: CustomExposure) => AxisValue> = {
  flag: () => true,
  expr: (c) => c.expr.trim(),
  cond: (c) => c.condCode.trim(),
  loc: (c) => c.loc,
  number: (c) => c.value,
};

/**
 * Why a custom build cannot be written, or null when it can: an empty
 * expression, or a condition that is empty or carries a clock (a
 * condition value is TIMELESS - a trailing clock would be the exposure's).
 */
function refusal(kind: string, c: CustomExposure): string | null {
  if (kind === 'expr' && !c.expr.trim()) return 'no expression';
  const cond = c.condCode.trim();
  if (kind === 'cond' && (!cond || /:\d+$/.test(cond))) return 'no timeless condition';
  return null;
}

/** The custom build's code, or null when it cannot be written. */
export function customExposureCode(c: CustomExposure, shared: SharedExposureFields): string | null {
  const kind = AXES.find((a) => a.id === c.axis)!.kind;
  if (refusal(kind, c)) return null;
  const { scope, mode, uses } = shared;
  return buildExposureCode({
    name: 'custom',
    axes: { [c.axis]: AXIS_VALUE[kind](c) },
    scope,
    rounds: c.rounds,
    ...(mode ? { mode } : {}),
    ...(c.radius ? { radius: c.radius } : {}),
    ...(uses ? { uses } : {}),
    ...(c.cap ? { cap: c.cap } : {}),
  });
}
